import type { DatasetQuestionsSearchResponse, QuestionDetail } from "@models/Dataset";
import type {
  ScorecardBrand,
  ScorecardBrandCandidate,
  ScorecardBrandMapping,
  ScorecardMappingStatus,
  ScorecardResponseCategory,
  ScorecardRowResultType,
  ScorecardSourceQuestionKind,
} from "@models/Scorecard";

/**
 * Detects whether a question can produce Scorecard brand candidates and, if so,
 * which shape they come from:
 * - Single grid (Single question, >=2 row variables AND >=2 values): brands are
 *   the Grid Variables.
 * - One-dimensional Multiple: each response option is its own variable, so
 *   brands are the question's variables.
 * - One-dimensional Single: brands are the question's values (response categories).
 * Returns null for any other question type (Number, Float, Text, or a Single
 * question with neither shape).
 */
/** The search/list endpoint returns abbreviated type codes ("MULTI", "NUMERIC");
 *  the detail endpoint returns the full word ("MULTIPLE", "NUMBER"). Normalize both. */
export function normalizeQuestionType(type: string | null | undefined): string {
  const upper = type?.toUpperCase() ?? "";
  if (upper === "MULTI") return "MULTIPLE";
  if (upper === "NUMERIC") return "NUMBER";
  return upper;
}

export function getSourceQuestionKind(
  question: QuestionDetail,
): ScorecardSourceQuestionKind | null {
  const type = normalizeQuestionType(question.type);
  const rowVariableCount = question.rowVariables?.length ?? 0;
  const valuesCount = question.valuesCount ?? question.values?.length ?? 0;

  if (type === "SINGLE" && rowVariableCount >= 2 && valuesCount >= 2) {
    return "single_grid";
  }
  if (type === "SINGLE" || type === "MULTIPLE" || type === "NUMBER" || type === "FLOAT") {
    return "one_dimensional";
  }
  return null;
}

/** Percentage for Single/Multiple questions, Mean for Number/Float — null otherwise. */
export function getMetricResultType(
  question: QuestionDetail,
): ScorecardRowResultType | null {
  const type = normalizeQuestionType(question.type);
  if (type === "SINGLE" || type === "MULTIPLE") return "percentage";
  if (type === "NUMBER" || type === "FLOAT") return "mean";
  return null;
}

/** Brand candidates in source-question order, or [] if the question is incompatible. */
export function getBrandCandidates(
  question: QuestionDetail,
): ScorecardBrandCandidate[] {
  const kind = getSourceQuestionKind(question);
  if (!kind) return [];
  const type = normalizeQuestionType(question.type);

  if (kind === "single_grid" || type === "MULTIPLE" || type === "NUMBER" || type === "FLOAT") {
    return (question.rowVariables ?? []).map((v, i) => ({
      sourceId: v.id,
      label: v.rowtitle || v.title,
      matchLabels: dedupeLabels([v.rowtitle, v.title, v.label]),
      sourceOrder: i,
    }));
  }

  return (question.values ?? []).map((v, i) => ({
    sourceId: v.id,
    label: v.title || v.label,
    matchLabels: dedupeLabels([v.title, v.label]),
    sourceOrder: i,
  }));
}

/** Response-data endpoints don't always echo back the same label field the
 *  question-detail endpoint used (e.g. `rowtitle` vs plain `title`) — keep
 *  every non-empty variant so matching against fetched data can try them all. */
function dedupeLabels(labels: Array<string | null | undefined>): string[] {
  return Array.from(new Set(labels.filter((l): l is string => !!l)));
}

/** A Source Brand Question / Question row's source question must expose >=2 brand candidates. */
export function isEligibleSourceBrandQuestion(question: QuestionDetail): boolean {
  return getBrandCandidates(question).length >= 2;
}

/**
 * Cheap list-level eligibility check using only the counts the search/list
 * endpoint returns (no rowVariables/values arrays available yet). Single
 * covers both one-dimensional and grid brand shapes since both require
 * valuesCount >= 2; Multiple's brands are its variables. Used for the Source
 * Brand Question picker — Number/Float are never valid Source Brand Questions.
 */
export function isEligibleSourceBrandQuestionSummary(
  question: DatasetQuestionsSearchResponse,
): boolean {
  const type = normalizeQuestionType(question.type);
  if (type === "SINGLE") return (question.valuesCount ?? 0) >= 2;
  if (type === "MULTIPLE") return (question.variablesCount ?? 0) >= 2;
  return false;
}

/**
 * Same shape check as isEligibleSourceBrandQuestionSummary, but also allows
 * Number/Float (Mean rows) — used for the Question row's own question
 * picker, which is a superset of what's valid for the Source Brand Question.
 */
export function isEligibleQuestionRowQuestionSummary(
  question: DatasetQuestionsSearchResponse,
): boolean {
  if (isEligibleSourceBrandQuestionSummary(question)) return true;
  const type = normalizeQuestionType(question.type);
  return (
    (type === "NUMBER" || type === "FLOAT") && (question.variablesCount ?? 0) >= 2
  );
}

export function normalizeLabel(label: string): string {
  return label.trim().toLowerCase().replace(/\s+/g, " ");
}

/** Normalized lookup keys for a category — its display label plus every
 *  match-label variant, so a brand named after either survives auto-match. */
function matchKeysFor(cat: MatchableCategory): string[] {
  const keys = [cat.label, ...(cat.matchLabels ?? [])].map(normalizeLabel);
  return Array.from(new Set(keys));
}

export type MatchableCategory = ScorecardResponseCategory;

/** Adapts brand-candidate shape (sourceId/label) to the matcher's id/label shape. */
export function toMatchableCategories(
  candidates: ScorecardBrandCandidate[],
): MatchableCategory[] {
  return candidates.map((c) => ({ id: c.sourceId, label: c.label, matchLabels: c.matchLabels }));
}

/** Response categories a brand can bind to in a manual-mapping dropdown:
 *  every category, minus ones already claimed by another brand (its own
 *  current binding, if any, stays available so re-selecting it works). */
export function availableOptionsFor(
  brandId: string,
  mappings: ScorecardBrandMapping[],
  responseCategories: MatchableCategory[],
): { value: string; label: string }[] {
  const currentBinding = mappings.find((m) => m.brandId === brandId)?.boundResponseId;
  const usedElsewhere = new Set(
    mappings.filter((m) => m.brandId !== brandId && m.boundResponseId).map((m) => m.boundResponseId),
  );
  return responseCategories
    .filter((c) => !usedElsewhere.has(c.id) || c.id === currentBinding)
    .map((c) => ({ value: c.id, label: c.label }));
}

/** Worse-first ranking so a displayed status always reflects whichever side
 *  needs more attention — matched < manual < unresolved < unavailable. */
const MAPPING_STATUS_SEVERITY: Record<ScorecardMappingStatus, number> = {
  matched: 0,
  manual: 1,
  unresolved: 2,
  unavailable: 3,
};

/** Combines a row's own brand mapping status with its Scorecard Row Base's
 *  status for the same brand: whichever is worse wins. A row's own mapping
 *  can look fine (matched/manual) while the Base it depends on is in a worse
 *  state for that brand (also manually mapped, unresolved, or unavailable) —
 *  used by both the Rows list (RowListItem) and the Brand Mappings panel
 *  inside the row editor (MappingRow), so the two surfaces never disagree
 *  about the same brand. */
export function getEffectiveMappingStatus(
  status: ScorecardMappingStatus,
  baseStatus: ScorecardMappingStatus | undefined,
): ScorecardMappingStatus {
  if (baseStatus !== undefined && MAPPING_STATUS_SEVERITY[baseStatus] > MAPPING_STATUS_SEVERITY[status]) {
    return baseStatus;
  }
  return status;
}

/**
 * Auto-matches each Scorecard brand to a response category by normalized-label
 * equality. One-to-one: once a category is claimed by a brand, it can't be
 * claimed by another. Zero or multiple candidate matches leave the brand
 * "unresolved" for the researcher to resolve manually.
 */
export function autoMatchBrands(
  brands: ScorecardBrand[],
  responseCategories: MatchableCategory[],
): ScorecardBrandMapping[] {
  const byNormalizedLabel = new Map<string, MatchableCategory[]>();
  responseCategories.forEach((cat) => {
    matchKeysFor(cat).forEach((key) => {
      const existing = byNormalizedLabel.get(key) ?? [];
      if (!existing.includes(cat)) byNormalizedLabel.set(key, [...existing, cat]);
    });
  });

  const usedResponseIds = new Set<string>();

  return brands.map((brand): ScorecardBrandMapping => {
    const candidates = byNormalizedLabel.get(normalizeLabel(brand.label)) ?? [];
    const available = candidates.filter((c) => !usedResponseIds.has(c.id));

    if (available.length === 1) {
      usedResponseIds.add(available[0].id);
      return {
        brandId: brand.id,
        status: "matched",
        boundResponseId: available[0].id,
        boundResponseLabel: available[0].label,
        matchLabels: available[0].matchLabels,
      };
    }

    return {
      brandId: brand.id,
      status: "unresolved",
      boundResponseId: null,
      boundResponseLabel: null,
    };
  });
}

/**
 * Attempts to auto-match a single newly-added brand against a row's own
 * response categories, without disturbing any of the row's existing
 * mappings — used when a new Competitor Brand is added to an already-defined
 * Scorecard. Same normalized-label, one-to-one rule as autoMatchBrands, but
 * scoped to just this brand against whatever categories the row's other
 * mappings haven't already claimed.
 */
export function autoMatchNewBrand(
  brand: ScorecardBrand,
  responseCategories: MatchableCategory[],
  existingMappings: ScorecardBrandMapping[],
): ScorecardBrandMapping {
  const usedResponseIds = new Set(
    existingMappings
      .map((m) => m.boundResponseId)
      .filter((id): id is string => !!id),
  );
  const normalizedBrandLabel = normalizeLabel(brand.label);
  const candidates = responseCategories.filter(
    (c) => !usedResponseIds.has(c.id) && matchKeysFor(c).includes(normalizedBrandLabel),
  );

  if (candidates.length === 1) {
    return {
      brandId: brand.id,
      status: "matched",
      boundResponseId: candidates[0].id,
      boundResponseLabel: candidates[0].label,
      matchLabels: candidates[0].matchLabels,
    };
  }

  return {
    brandId: brand.id,
    status: "unresolved",
    boundResponseId: null,
    boundResponseLabel: null,
  };
}

// Row-lifecycle helpers (brand add/remove reconciliation, dependent-row
// tracking, duplicate/equality checks) live in scorecard-row-lifecycle-utils.ts
// — kept separate to stay under the file-size limit.
